// ===================================================
// Built-in Global Fields — Seed Data
// ===================================================
// These are the default fields available in every vBrain instance.
// Users can create more via the Field Library UI.

import type { GlobalFieldInsert } from './types';

// ─── System Fields Metadata ───────────────────────────
// Maps system field keys to NoteRecord columns for the universal strip.
export const SYSTEM_FIELDS: {
  key: string;
  noteField: string;
  label: { he: string; en: string; ru: string };
  icon: string;
}[] = [
  { key: 'sys_title', noteField: 'title', label: { he: 'כותרת', en: 'Title', ru: 'Заголовок' }, icon: 'Type' },
  { key: 'sys_status', noteField: 'status', label: { he: 'סטטוס', en: 'Status', ru: 'Статус' }, icon: 'CircleDot' },
  { key: 'sys_created_by', noteField: 'created_by', label: { he: 'נוצר על ידי', en: 'Created By', ru: 'Создано' }, icon: 'UserCircle' },
  { key: 'sys_created_at', noteField: 'created_at', label: { he: 'תאריך יצירה', en: 'Created At', ru: 'Дата создания' }, icon: 'Calendar' },
  { key: 'sys_last_edited_at', noteField: 'last_edited_at', label: { he: 'עודכן לאחרונה', en: 'Last Edited', ru: 'Последнее изменение' }, icon: 'Clock' },
];

// Universal fields — always shown on every entity, regardless of field_refs.
// Extended 2026-07-28 (Gal): every instance should carry its meta fields
// (who/when created + edited) and the five OS-level capability modules
// (vTask/vFile/vNote/vCollection/vRule) without per-entity-type configuration.
export const UNIVERSAL_FIELD_KEYS = [
  'status', 'assignee', 'priority',
  'sys_created_by', 'sys_created_at', 'sys_last_edited_by', 'sys_last_edited_at',
  'vtask', 'vfile', 'vnote', 'vcollection', 'vrule',
  // vtag joined 2026-08-10 when its module went live (DEC-0035 — one system
  // field, three internal levels; the sixth v-kind was registered in
  // global_fields all along but had no renderer until now).
  'vtag',
  // Task planning fields joined 2026-09-20 (Gal, DEC-0452 questionnaire): any
  // instance can be sized, scored and blocked, not only the `task` type.
  // decision_score and blocked are derived, read-only rows (never stored).
  'effort', 'impact', 'decision_score', 'blocked',
] as const;

// ─── Which universal fields actually reach the screen ─────
// Registering a key above only promises it to every entity type; it does not
// promise anyone can use it. Two groups reach NoteMeta with nothing behind
// them, and both were rendering as editable text boxes in the flat grid:
//
//   • v-kinds with no module — global_fields lists field_module for all six,
//     but only vtask and vtag have a renderer (see FieldEditor's switch).
//     The other four hit the plain-text default: a box labelled "קבצים" that
//     saves a string nobody reads.
//   • sys_* — the audit trail. The real values live in vb_records columns and
//     the entity detail page already prints them read-only from SYSTEM_FIELDS
//     above. The meta copies are decoys pointing at a different key.
//
// Measured 2026-08-12 across all 362 live vb_records: zero rows carry a value
// under any of the eight, so hiding them loses no data. Gal's rule, same day:
// a universal field is shown if and only if something can actually render it
// and it is not a second, editable copy of a read-only strip.
//
// This is deliberately keyed on field_type/meta_key rather than an allow-list
// of what to show: the day vfile-module ships, deleting one entry here lights
// the field up everywhere it is claimed. No per-type config to chase.
const MODULE_LESS_FIELD_TYPES = new Set(['vfile', 'vnote', 'vcollection', 'vrule']);
const AUDIT_MIRROR_FIELD_KEYS = new Set([
  'sys_created_by', 'sys_created_at', 'sys_last_edited_by', 'sys_last_edited_at',
]);

/**
 * True when this field has a working editor and is worth a slot on an
 * instance screen. Applies to every field, not just universal ones — a
 * module-less v-kind is just as dead when a template claims it by name
 * (`contact` claims vfile + vnote today).
 */
export function isFieldRenderable(field: { meta_key: string; field_type: string }): boolean {
  if (MODULE_LESS_FIELD_TYPES.has(field.field_type)) return false;
  if (AUDIT_MIRROR_FIELD_KEYS.has(field.meta_key)) return false;
  return true;
}

export const BUILTIN_FIELDS: GlobalFieldInsert[] = [
  // ─── System Fields (always present, cannot be deleted) ──
  {
    meta_key: 'sys_title',
    label: { he: 'כותרת', en: 'Title', ru: 'Заголовок' },
    description: { he: 'כותרת הפתק', en: 'Note title', ru: 'Заголовок записки' },
    field_type: 'text',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { required: true },
    default_value: null,
    icon: 'Type',
    category: 'system',
    sort_order: -5,
  },
  {
    meta_key: 'sys_status',
    label: { he: 'סטטוס', en: 'Status', ru: 'Статус' },
    description: { he: 'סטטוס הפתק', en: 'Note status', ru: 'Статус записки' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'active', label: { he: 'פעיל', en: 'Active', ru: 'Активный' }, color: '#34d399' },
      { value: 'inactive', label: { he: 'לא פעיל', en: 'Inactive', ru: 'Неактивный' }, color: '#ef4444' },
    ],
    validation: {},
    default_value: 'active',
    icon: 'CircleDot',
    category: 'system',
    sort_order: -4,
  },
  {
    meta_key: 'sys_created_by',
    label: { he: 'נוצר על ידי', en: 'Created By', ru: 'Создано' },
    description: { he: 'מי יצר את הפתק', en: 'Who created the note', ru: 'Кто создал записку' },
    field_type: 'person',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'UserCircle',
    category: 'system',
    sort_order: -3,
  },
  {
    meta_key: 'sys_created_at',
    label: { he: 'תאריך יצירה', en: 'Created At', ru: 'Дата создания' },
    description: { he: 'מתי נוצר הפתק', en: 'When the note was created', ru: 'Когда была создана записка' },
    field_type: 'date',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'Calendar',
    category: 'system',
    sort_order: -2,
  },
  {
    meta_key: 'sys_last_edited_at',
    label: { he: 'עודכן לאחרונה', en: 'Last Edited', ru: 'Последнее изменение' },
    description: { he: 'מתי עודכן הפתק לאחרונה', en: 'When the note was last edited', ru: 'Когда записка была изменена' },
    field_type: 'date',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'Clock',
    category: 'system',
    sort_order: -1,
  },
  {
    meta_key: 'sys_last_edited_by',
    label: { he: 'עודכן על ידי', en: 'Last Edited By', ru: 'Изменено' },
    description: { he: 'מי עדכן לאחרונה', en: 'Who last edited the note', ru: 'Кто последний раз редактировал' },
    field_type: 'person',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'UserCircle',
    category: 'system',
    sort_order: -2,
  },

  // ─── Composite Fields ─────────────────────────────
  {
    meta_key: 'full_name',
    label: { he: 'שם מלא', en: 'Full Name', ru: 'Полное имя' },
    description: { he: 'שם פרטי + שם משפחה', en: 'First name + last name', ru: 'Имя + фамилия' },
    field_type: 'composite',
    is_composite: true,
    sub_fields: [
      { meta_key: 'first_name', label: { he: 'שם פרטי', en: 'First Name', ru: 'Имя' }, field_type: 'text' },
      { meta_key: 'last_name', label: { he: 'שם משפחה', en: 'Last Name', ru: 'Фамилия' }, field_type: 'text' },
    ],
    display_template: '{first_name} {last_name}',
    options: [],
    validation: {},
    default_value: null,
    icon: 'User',
    category: 'contact',
    sort_order: 0,
  },
  {
    meta_key: 'business',
    label: { he: 'בית עסק', en: 'Business', ru: 'Бизнес' },
    description: { he: 'שם עסק + מספר זיהוי', en: 'Business name + ID number', ru: 'Название + ID' },
    field_type: 'composite',
    is_composite: true,
    sub_fields: [
      { meta_key: 'business_name', label: { he: 'שם העסק', en: 'Business Name', ru: 'Название' }, field_type: 'text' },
      { meta_key: 'business_id', label: { he: 'ח.פ / ע.מ', en: 'Business ID', ru: 'ИНН' }, field_type: 'text' },
    ],
    display_template: '{business_name} ({business_id})',
    options: [],
    validation: {},
    default_value: null,
    icon: 'Building2',
    category: 'business',
    sort_order: 1,
  },
  {
    meta_key: 'address',
    label: { he: 'כתובת', en: 'Address', ru: 'Адрес' },
    description: { he: 'רחוב + עיר + מיקוד', en: 'Street + city + zip', ru: 'Улица + город + индекс' },
    field_type: 'composite',
    is_composite: true,
    sub_fields: [
      { meta_key: 'street', label: { he: 'רחוב', en: 'Street', ru: 'Улица' }, field_type: 'text' },
      { meta_key: 'city', label: { he: 'עיר', en: 'City', ru: 'Город' }, field_type: 'text' },
      { meta_key: 'zip', label: { he: 'מיקוד', en: 'Zip', ru: 'Индекс' }, field_type: 'text' },
    ],
    display_template: '{street}, {city} {zip}',
    options: [],
    validation: {},
    default_value: null,
    icon: 'MapPin',
    category: 'contact',
    sort_order: 2,
  },

  // ─── Simple Fields ────────────────────────────────
  {
    meta_key: 'phone',
    label: { he: 'טלפון', en: 'Phone', ru: 'Телефон' },
    description: { he: 'מספר טלפון', en: 'Phone number', ru: 'Номер телефона' },
    field_type: 'phone',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'Phone',
    category: 'contact',
    sort_order: 10,
  },
  {
    meta_key: 'email',
    label: { he: 'מייל', en: 'Email', ru: 'Электронная почта' },
    description: { he: 'כתובת אימייל', en: 'Email address', ru: 'Адрес электронной почты' },
    field_type: 'email',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'Mail',
    category: 'contact',
    sort_order: 11,
  },
  {
    meta_key: 'status',
    label: { he: 'סטטוס', en: 'Status', ru: 'Статус' },
    description: { he: 'סטטוס נוכחי', en: 'Current status', ru: 'Текущий статус' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'todo', label: { he: 'לביצוע', en: 'To Do', ru: 'К выполнению' }, color: '#60a5fa' },
      { value: 'in-progress', label: { he: 'בביצוע', en: 'In Progress', ru: 'В работе' }, color: '#f472b6' },
      { value: 'done', label: { he: 'הושלם', en: 'Done', ru: 'Готово' }, color: '#34d399' },
      { value: 'cancelled', label: { he: 'בוטל', en: 'Cancelled', ru: 'Отменено' }, color: '#ef4444' },
    ],
    validation: {},
    default_value: 'todo',
    icon: 'CircleDot',
    category: 'general',
    sort_order: 20,
  },
  {
    meta_key: 'priority',
    label: { he: 'עדיפות', en: 'Priority', ru: 'Приоритет' },
    description: { he: 'רמת עדיפות', en: 'Priority level', ru: 'Уровень приоритета' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'low', label: { he: 'נמוכה', en: 'Low', ru: 'Низкий' }, color: '#94a3b8' },
      { value: 'medium', label: { he: 'בינונית', en: 'Medium', ru: 'Средний' }, color: '#fbbf24' },
      { value: 'high', label: { he: 'גבוהה', en: 'High', ru: 'Высокий' }, color: '#fb923c' },
      { value: 'urgent', label: { he: 'דחופה', en: 'Urgent', ru: 'Срочный' }, color: '#ef4444' },
    ],
    validation: {},
    default_value: null,
    icon: 'Flag',
    category: 'general',
    sort_order: 21,
  },
  {
    meta_key: 'due_date',
    label: { he: 'תאריך יעד', en: 'Due Date', ru: 'Срок' },
    description: { he: 'תאריך יעד לסיום', en: 'Target completion date', ru: 'Целевая дата завершения' },
    field_type: 'date',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'CalendarDays',
    category: 'general',
    sort_order: 22,
  },
  {
    meta_key: 'assignee',
    label: { he: 'אחראי', en: 'Assignee', ru: 'Ответственный' },
    description: { he: 'האדם האחראי', en: 'Person responsible', ru: 'Ответственное лицо' },
    field_type: 'person',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'UserCircle',
    category: 'general',
    sort_order: 23,
  },
  {
    meta_key: 'tags',
    label: { he: 'תגיות', en: 'Tags', ru: 'Теги' },
    description: { he: 'תגיות לסיווג', en: 'Classification tags', ru: 'Теги классификации' },
    field_type: 'multi-select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'Tags',
    category: 'general',
    sort_order: 24,
  },
  {
    meta_key: 'url',
    label: { he: 'קישור', en: 'URL', ru: 'Ссылка' },
    description: { he: 'כתובת אינטרנט', en: 'Web address', ru: 'Веб-адрес' },
    field_type: 'url',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'Link',
    category: 'general',
    sort_order: 25,
  },
  {
    meta_key: 'notes',
    label: { he: 'הערות', en: 'Notes', ru: 'Заметки' },
    description: { he: 'הערות חופשיות', en: 'Free-form notes', ru: 'Свободные заметки' },
    field_type: 'text',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'StickyNote',
    category: 'general',
    sort_order: 26,
  },
  {
    meta_key: 'is_active',
    label: { he: 'פעיל', en: 'Active', ru: 'Активен' },
    description: { he: 'האם פעיל?', en: 'Is active?', ru: 'Активен?' },
    field_type: 'checkbox',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: true,
    icon: 'CheckSquare',
    category: 'general',
    sort_order: 27,
  },

  // ─── Dev / Project Fields ──────────────────────────
  {
    meta_key: 'sprint',
    label: { he: 'ספרינט', en: 'Sprint', ru: 'Спринт' },
    description: { he: 'ספרינט נוכחי', en: 'Current sprint', ru: 'Текущий спринт' },
    field_type: 'text',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'Zap',
    category: 'project',
    sort_order: 30,
  },
  {
    meta_key: 'story_points',
    label: { he: 'נקודות סיפור', en: 'Story Points', ru: 'Story Points' },
    description: { he: 'הערכת מאמץ', en: 'Effort estimate', ru: 'Оценка трудозатрат' },
    field_type: 'number',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0, max: 100 },
    default_value: null,
    icon: 'Hash',
    category: 'project',
    sort_order: 31,
  },
  {
    meta_key: 'acceptance_criteria',
    label: { he: 'קריטריוני קבלה', en: 'Acceptance Criteria', ru: 'Критерии приёмки' },
    description: { he: 'תנאי סיום', en: 'Definition of done', ru: 'Критерии завершения' },
    field_type: 'text',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'ListChecks',
    category: 'project',
    sort_order: 32,
  },
  {
    meta_key: 'pr_link',
    label: { he: 'קישור PR', en: 'PR Link', ru: 'Ссылка PR' },
    description: { he: 'קישור ל-Pull Request', en: 'Pull Request link', ru: 'Ссылка на Pull Request' },
    field_type: 'url',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'GitPullRequest',
    category: 'project',
    sort_order: 33,
  },
  {
    meta_key: 'environment',
    label: { he: 'סביבה', en: 'Environment', ru: 'Среда' },
    description: { he: 'סביבת פריסה', en: 'Deployment environment', ru: 'Среда развёртывания' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'dev', label: { he: 'פיתוח', en: 'Dev', ru: 'Разработка' }, color: '#60a5fa' },
      { value: 'staging', label: { he: 'סטיגינג', en: 'Staging', ru: 'Стейджинг' }, color: '#fbbf24' },
      { value: 'production', label: { he: 'פרודקשן', en: 'Production', ru: 'Продакшн' }, color: '#34d399' },
    ],
    validation: {},
    default_value: null,
    icon: 'Server',
    category: 'project',
    sort_order: 34,
  },
  {
    meta_key: 'branch',
    label: { he: 'ענף', en: 'Branch', ru: 'Ветка' },
    description: { he: 'שם הענף ב-Git', en: 'Git branch name', ru: 'Имя ветки Git' },
    field_type: 'text',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'GitBranch',
    category: 'project',
    sort_order: 35,
  },
  {
    meta_key: 'reviewer',
    label: { he: 'סוקר', en: 'Reviewer', ru: 'Ревьюер' },
    description: { he: 'סוקר הקוד', en: 'Code reviewer', ru: 'Ревьюер кода' },
    field_type: 'person',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'UserCheck',
    category: 'project',
    sort_order: 36,
  },
  {
    meta_key: 'start_date',
    label: { he: 'תאריך התחלה', en: 'Start Date', ru: 'Дата начала' },
    description: { he: 'תאריך תחילת העבודה', en: 'Work start date', ru: 'Дата начала работы' },
    field_type: 'date',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'CalendarPlus',
    category: 'project',
    sort_order: 37,
  },
  {
    meta_key: 'estimated_hours',
    label: { he: 'שעות מוערכות', en: 'Estimated Hours', ru: 'Оценка часов' },
    description: { he: 'הערכת זמן בשעות', en: 'Time estimate in hours', ru: 'Оценка времени в часах' },
    field_type: 'number',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0 },
    default_value: null,
    icon: 'Clock',
    category: 'project',
    sort_order: 38,
  },
  {
    meta_key: 'actual_hours',
    label: { he: 'שעות בפועל', en: 'Actual Hours', ru: 'Фактические часы' },
    description: { he: 'זמן בפועל בשעות', en: 'Actual time spent in hours', ru: 'Фактическое время в часах' },
    field_type: 'number',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0 },
    default_value: null,
    icon: 'Timer',
    category: 'project',
    sort_order: 39,
  },

  // ─── CRM / Finance Fields ─────────────────────────
  {
    meta_key: 'deal_value',
    label: { he: 'ערך עסקה', en: 'Deal Value', ru: 'Сумма сделки' },
    description: { he: 'ערך כספי של העסקה', en: 'Monetary deal value', ru: 'Денежная сумма сделки' },
    field_type: 'number',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0 },
    default_value: null,
    icon: 'DollarSign',
    category: 'finance',
    sort_order: 40,
  },
  {
    meta_key: 'expected_close',
    label: { he: 'סגירה צפויה', en: 'Expected Close', ru: 'Ожидаемое закрытие' },
    description: { he: 'תאריך סגירה צפוי', en: 'Expected close date', ru: 'Ожидаемая дата закрытия' },
    field_type: 'date',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'CalendarCheck',
    category: 'finance',
    sort_order: 41,
  },
  {
    meta_key: 'pipeline_stage',
    label: { he: 'שלב צנרת', en: 'Pipeline Stage', ru: 'Этап воронки' },
    description: { he: 'שלב בצנרת המכירות', en: 'Sales pipeline stage', ru: 'Этап воронки продаж' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'lead', label: { he: 'ליד', en: 'Lead', ru: 'Лид' }, color: '#94a3b8' },
      { value: 'qualified', label: { he: 'מוסמך', en: 'Qualified', ru: 'Квалифицирован' }, color: '#60a5fa' },
      { value: 'proposal', label: { he: 'הצעה', en: 'Proposal', ru: 'Предложение' }, color: '#a78bfa' },
      { value: 'negotiation', label: { he: 'משא ומתן', en: 'Negotiation', ru: 'Переговоры' }, color: '#fbbf24' },
      { value: 'closed_won', label: { he: 'נסגר ✓', en: 'Closed Won', ru: 'Закрыто ✓' }, color: '#34d399' },
      { value: 'closed_lost', label: { he: 'נסגר ✗', en: 'Closed Lost', ru: 'Закрыто ✗' }, color: '#ef4444' },
    ],
    validation: {},
    default_value: 'lead',
    icon: 'TrendingUp',
    category: 'finance',
    sort_order: 42,
  },
  {
    meta_key: 'probability',
    label: { he: 'הסתברות', en: 'Probability', ru: 'Вероятность' },
    description: { he: 'הסתברות סגירה (%)', en: 'Close probability (%)', ru: 'Вероятность закрытия (%)' },
    field_type: 'number',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0, max: 100 },
    default_value: null,
    icon: 'Percent',
    category: 'finance',
    sort_order: 43,
  },
  {
    meta_key: 'commission',
    label: { he: 'עמלה', en: 'Commission', ru: 'Комиссия' },
    description: { he: 'עמלה על העסקה', en: 'Deal commission', ru: 'Комиссия по сделке' },
    field_type: 'number',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0 },
    default_value: null,
    icon: 'Coins',
    category: 'finance',
    sort_order: 44,
  },
  {
    meta_key: 'lead_source',
    label: { he: 'מקור ליד', en: 'Lead Source', ru: 'Источник лида' },
    description: { he: 'מאיפה הגיע הליד', en: 'Where the lead came from', ru: 'Откуда пришёл лид' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'website', label: { he: 'אתר', en: 'Website', ru: 'Сайт' } },
      { value: 'referral', label: { he: 'הפניה', en: 'Referral', ru: 'Рекомендация' } },
      { value: 'cold_call', label: { he: 'שיחה קרה', en: 'Cold Call', ru: 'Холодный звонок' } },
      { value: 'event', label: { he: 'אירוע', en: 'Event', ru: 'Мероприятие' } },
      { value: 'ad', label: { he: 'פרסום', en: 'Ad', ru: 'Реклама' } },
    ],
    validation: {},
    default_value: null,
    icon: 'Megaphone',
    category: 'finance',
    sort_order: 45,
  },
  {
    meta_key: 'qualification_score',
    label: { he: 'ציון כשירות', en: 'Qualification Score', ru: 'Оценка квалификации' },
    description: { he: 'ציון כשירות 1-10', en: 'Qualification score 1-10', ru: 'Оценка квалификации 1-10' },
    field_type: 'number',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 1, max: 10 },
    default_value: null,
    icon: 'Star',
    category: 'finance',
    sort_order: 46,
  },
  {
    meta_key: 'conversion_date',
    label: { he: 'תאריך המרה', en: 'Conversion Date', ru: 'Дата конверсии' },
    description: { he: 'מתי הליד הפך לעסקה', en: 'When lead converted to deal', ru: 'Когда лид стал сделкой' },
    field_type: 'date',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'ArrowRightCircle',
    category: 'finance',
    sort_order: 47,
  },
  {
    meta_key: 'agreement_type',
    label: { he: 'סוג הסכם', en: 'Agreement Type', ru: 'Тип соглашения' },
    description: { he: 'סוג ההסכם', en: 'Type of agreement', ru: 'Тип соглашения' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'service', label: { he: 'שירות', en: 'Service', ru: 'Сервис' } },
      { value: 'rental', label: { he: 'השכרה', en: 'Rental', ru: 'Аренда' } },
      { value: 'sale', label: { he: 'מכירה', en: 'Sale', ru: 'Продажа' } },
      { value: 'partnership', label: { he: 'שותפות', en: 'Partnership', ru: 'Партнёрство' } },
    ],
    validation: {},
    default_value: null,
    icon: 'FileSignature',
    category: 'finance',
    sort_order: 48,
  },
  {
    meta_key: 'effective_date',
    label: { he: 'תאריך תחילה', en: 'Effective Date', ru: 'Дата вступления' },
    description: { he: 'תאריך תחילת ההסכם', en: 'Agreement effective date', ru: 'Дата вступления в силу' },
    field_type: 'date',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'CalendarRange',
    category: 'finance',
    sort_order: 49,
  },
  {
    meta_key: 'expiry_date',
    label: { he: 'תאריך תפוגה', en: 'Expiry Date', ru: 'Дата истечения' },
    description: { he: 'תאריך תפוגת ההסכם', en: 'Agreement expiry date', ru: 'Дата истечения соглашения' },
    field_type: 'date',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'CalendarX',
    category: 'finance',
    sort_order: 50,
  },

  // ═══════════════════════════════════════════════════
  // ─── HR Fields ─────────────────────────────────────
  // ═══════════════════════════════════════════════════
  {
    meta_key: 'role',
    label: { he: 'תפקיד', en: 'Role', ru: 'Должность' },
    description: { he: 'תפקיד העובד', en: 'Employee role', ru: 'Должность сотрудника' },
    field_type: 'text',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'BadgeCheck',
    category: 'hr',
    sort_order: 50,
  },
  {
    meta_key: 'department',
    label: { he: 'מחלקה', en: 'Department', ru: 'Отдел' },
    description: { he: 'מחלקת העובד', en: 'Employee department', ru: 'Отдел сотрудника' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'management', label: { he: 'הנהלה', en: 'Management', ru: 'Руководство' }, color: '#a78bfa' },
      { value: 'operations', label: { he: 'תפעול', en: 'Operations', ru: 'Операции' }, color: '#60a5fa' },
      { value: 'sales', label: { he: 'מכירות', en: 'Sales', ru: 'Продажи' }, color: '#34d399' },
      { value: 'finance', label: { he: 'כספים', en: 'Finance', ru: 'Финансы' }, color: '#fbbf24' },
      { value: 'hr', label: { he: 'משאבי אנוש', en: 'HR', ru: 'HR' }, color: '#f472b6' },
      { value: 'development', label: { he: 'פיתוח', en: 'Development', ru: 'Разработка' }, color: '#818cf8' },
      { value: 'marketing', label: { he: 'שיווק', en: 'Marketing', ru: 'Маркетинг' }, color: '#fb923c' },
      { value: 'field', label: { he: 'שטח', en: 'Field', ru: 'Полевой' }, color: '#22d3ee' },
    ],
    validation: {},
    default_value: null,
    icon: 'Building',
    category: 'hr',
    sort_order: 51,
  },
  {
    meta_key: 'hire_date',
    label: { he: 'תאריך תחילת עבודה', en: 'Hire Date', ru: 'Дата приёма' },
    description: { he: 'מתי התחיל לעבוד', en: 'Employment start date', ru: 'Дата начала работы' },
    field_type: 'date',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { required: true },
    default_value: null,
    icon: 'CalendarPlus',
    category: 'hr',
    sort_order: 52,
  },
  {
    meta_key: 'salary',
    label: { he: 'שכר', en: 'Salary', ru: 'Зарплата' },
    description: { he: 'שכר חודשי ברוטו', en: 'Monthly gross salary', ru: 'Месячная зарплата брутто' },
    field_type: 'number',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0 },
    default_value: null,
    icon: 'Banknote',
    category: 'hr',
    sort_order: 53,
    read_only: true,
  },
  {
    meta_key: 'employee_status',
    label: { he: 'סטטוס עובד', en: 'Employee Status', ru: 'Статус сотрудника' },
    description: { he: 'מצב תעסוקתי נוכחי', en: 'Current employment status', ru: 'Текущий статус занятости' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'active', label: { he: 'פעיל', en: 'Active', ru: 'Активный' }, color: '#34d399' },
      { value: 'probation', label: { he: 'ניסיון', en: 'Probation', ru: 'Испытательный' }, color: '#fbbf24' },
      { value: 'vacation', label: { he: 'חופשה', en: 'Vacation', ru: 'Отпуск' }, color: '#60a5fa' },
      { value: 'sick', label: { he: 'מחלה', en: 'Sick Leave', ru: 'Больничный' }, color: '#fb923c' },
      { value: 'terminated', label: { he: 'סיים עבודה', en: 'Terminated', ru: 'Уволен' }, color: '#ef4444' },
    ],
    validation: {},
    default_value: 'active',
    icon: 'UserCheck',
    category: 'hr',
    sort_order: 54,
    color_rules: [
      { operator: 'eq', value: 'terminated', color: '#ef4444' },
      { operator: 'eq', value: 'sick', color: '#fb923c' },
      { operator: 'eq', value: 'probation', color: '#fbbf24' },
    ],
  },
  {
    meta_key: 'manager',
    label: { he: 'מנהל ישיר', en: 'Manager', ru: 'Руководитель' },
    description: { he: 'מנהל ישיר של העובד', en: 'Direct manager', ru: 'Непосредственный руководитель' },
    field_type: 'person',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'UserCog',
    category: 'hr',
    sort_order: 55,
  },
  {
    meta_key: 'vacation_days',
    label: { he: 'ימי חופשה', en: 'Vacation Days', ru: 'Дни отпуска' },
    description: { he: 'יתרת ימי חופשה', en: 'Remaining vacation days', ru: 'Остаток дней отпуска' },
    field_type: 'number',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0, max: 365 },
    default_value: null,
    icon: 'Palmtree',
    category: 'hr',
    sort_order: 56,
    color_rules: [
      { operator: 'lt', value: '3', color: '#ef4444' },
      { operator: 'lt', value: '7', color: '#fbbf24' },
    ],
  },
  {
    meta_key: 'id_number',
    label: { he: 'תעודת זהות', en: 'ID Number', ru: 'Удостоверение' },
    description: { he: 'מספר תעודת זהות', en: 'National ID number', ru: 'Номер удостоверения' },
    field_type: 'text',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { unique: true },
    default_value: null,
    icon: 'CreditCard',
    category: 'hr',
    sort_order: 57,
  },
  {
    meta_key: 'gender',
    label: { he: 'מגדר', en: 'Gender', ru: 'Пол' },
    description: { he: 'מגדר', en: 'Gender', ru: 'Пол' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'male', label: { he: 'זכר', en: 'Male', ru: 'Мужской' } },
      { value: 'female', label: { he: 'נקבה', en: 'Female', ru: 'Женский' } },
      { value: 'other', label: { he: 'אחר', en: 'Other', ru: 'Другой' } },
    ],
    validation: {},
    default_value: null,
    icon: 'User',
    category: 'hr',
    sort_order: 58,
  },

  // ═══════════════════════════════════════════════════
  // ─── Business Fields ───────────────────────────────
  // ═══════════════════════════════════════════════════
  {
    meta_key: 'business_type',
    label: { he: 'סוג עסק', en: 'Business Type', ru: 'Тип бизнеса' },
    description: { he: 'סוג ישות משפטית', en: 'Legal entity type', ru: 'Тип юридического лица' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'osek_morsheh', label: { he: 'עוסק מורשה', en: 'Authorized Dealer', ru: 'Авторизованный дилер' } },
      { value: 'osek_patur', label: { he: 'עוסק פטור', en: 'Exempt Dealer', ru: 'Освобождённый дилер' } },
      { value: 'ltd', label: { he: 'חברה בע״מ', en: 'Ltd. Company', ru: 'ООО' } },
      { value: 'partnership', label: { he: 'שותפות', en: 'Partnership', ru: 'Партнёрство' } },
      { value: 'amuta', label: { he: 'עמותה', en: 'Non-Profit', ru: 'Некоммерческая' } },
      { value: 'government', label: { he: 'ממשלתי', en: 'Government', ru: 'Государственное' } },
    ],
    validation: {},
    default_value: null,
    icon: 'Landmark',
    category: 'business',
    sort_order: 60,
  },
  {
    meta_key: 'industry',
    label: { he: 'ענף', en: 'Industry', ru: 'Отрасль' },
    description: { he: 'ענף פעילות', en: 'Industry sector', ru: 'Отрасль деятельности' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'construction', label: { he: 'בנייה', en: 'Construction', ru: 'Строительство' }, color: '#fb923c' },
      { value: 'real_estate', label: { he: 'נדל״ן', en: 'Real Estate', ru: 'Недвижимость' }, color: '#34d399' },
      { value: 'services', label: { he: 'שירותים', en: 'Services', ru: 'Услуги' }, color: '#60a5fa' },
      { value: 'technology', label: { he: 'טכנולוגיה', en: 'Technology', ru: 'Технология' }, color: '#818cf8' },
      { value: 'manufacturing', label: { he: 'ייצור', en: 'Manufacturing', ru: 'Производство' }, color: '#fbbf24' },
      { value: 'trade', label: { he: 'מסחר', en: 'Trade', ru: 'Торговля' }, color: '#f472b6' },
      { value: 'consulting', label: { he: 'ייעוץ', en: 'Consulting', ru: 'Консалтинг' }, color: '#a78bfa' },
      { value: 'infrastructure', label: { he: 'תשתיות', en: 'Infrastructure', ru: 'Инфраструктура' }, color: '#22d3ee' },
    ],
    validation: {},
    default_value: null,
    icon: 'Factory',
    category: 'business',
    sort_order: 61,
  },
  {
    meta_key: 'company_size',
    label: { he: 'גודל חברה', en: 'Company Size', ru: 'Размер компании' },
    description: { he: 'מספר עובדים', en: 'Number of employees', ru: 'Количество сотрудников' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'solo', label: { he: 'עצמאי', en: 'Solo', ru: 'Соло' } },
      { value: 'small', label: { he: '1-10', en: '1-10', ru: '1-10' } },
      { value: 'medium', label: { he: '11-50', en: '11-50', ru: '11-50' } },
      { value: 'large', label: { he: '51-200', en: '51-200', ru: '51-200' } },
      { value: 'enterprise', label: { he: '200+', en: '200+', ru: '200+' } },
    ],
    validation: {},
    default_value: null,
    icon: 'Users',
    category: 'business',
    sort_order: 62,
  },
  {
    meta_key: 'service_area',
    label: { he: 'אזור שירות', en: 'Service Area', ru: 'Зона обслуживания' },
    description: { he: 'אזורים גיאוגרפיים של פעילות', en: 'Geographic service areas', ru: 'Географические зоны обслуживания' },
    field_type: 'multi-select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'north', label: { he: 'צפון', en: 'North', ru: 'Север' }, color: '#60a5fa' },
      { value: 'haifa', label: { he: 'חיפה והקריות', en: 'Haifa Area', ru: 'Хайфа' }, color: '#34d399' },
      { value: 'sharon', label: { he: 'שרון', en: 'Sharon', ru: 'Шарон' }, color: '#a78bfa' },
      { value: 'center', label: { he: 'מרכז', en: 'Center', ru: 'Центр' }, color: '#fbbf24' },
      { value: 'tel_aviv', label: { he: 'תל אביב', en: 'Tel Aviv', ru: 'Тель-Авив' }, color: '#f472b6' },
      { value: 'jerusalem', label: { he: 'ירושלים', en: 'Jerusalem', ru: 'Иерусалим' }, color: '#fb923c' },
      { value: 'south', label: { he: 'דרום', en: 'South', ru: 'Юг' }, color: '#22d3ee' },
      { value: 'judea_samaria', label: { he: 'יו״ש', en: 'Judea & Samaria', ru: 'Иудея и Самария' }, color: '#94a3b8' },
      { value: 'nationwide', label: { he: 'ארצי', en: 'Nationwide', ru: 'По всей стране' }, color: '#818cf8' },
    ],
    validation: {},
    default_value: null,
    icon: 'MapPin',
    category: 'business',
    sort_order: 63,
  },

  // ═══════════════════════════════════════════════════
  // ─── Construction Fields ───────────────────────────
  // ═══════════════════════════════════════════════════
  {
    meta_key: 'project_type',
    label: { he: 'סוג פרויקט', en: 'Project Type', ru: 'Тип проекта' },
    description: { he: 'סוג פרויקט הבנייה', en: 'Construction project type', ru: 'Тип строительного проекта' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'residential', label: { he: 'מגורים', en: 'Residential', ru: 'Жилищное' }, color: '#60a5fa' },
      { value: 'commercial', label: { he: 'מסחרי', en: 'Commercial', ru: 'Коммерческое' }, color: '#34d399' },
      { value: 'industrial', label: { he: 'תעשייתי', en: 'Industrial', ru: 'Промышленное' }, color: '#fbbf24' },
      { value: 'infrastructure', label: { he: 'תשתיות', en: 'Infrastructure', ru: 'Инфраструктура' }, color: '#a78bfa' },
      { value: 'renovation', label: { he: 'שיפוץ', en: 'Renovation', ru: 'Реконструкция' }, color: '#fb923c' },
      { value: 'tama38', label: { he: 'תמ״א 38', en: 'TAMA 38', ru: 'ТАМА 38' }, color: '#f472b6' },
      { value: 'pinui_binui', label: { he: 'פינוי בינוי', en: 'Urban Renewal', ru: 'Городское обновление' }, color: '#22d3ee' },
    ],
    validation: {},
    default_value: null,
    icon: 'HardHat',
    category: 'construction',
    sort_order: 70,
  },
  {
    meta_key: 'completion_pct',
    label: { he: 'אחוז ביצוע', en: 'Completion %', ru: '% выполнения' },
    description: { he: 'אחוז השלמת הפרויקט', en: 'Project completion percentage', ru: 'Процент завершения проекта' },
    field_type: 'number',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0, max: 100 },
    default_value: 0,
    icon: 'CircleDot',
    category: 'construction',
    sort_order: 71,
    color_rules: [
      { operator: 'lt', value: '30', color: '#ef4444' },
      { operator: 'lt', value: '70', color: '#fbbf24' },
      { operator: 'gt', value: '69', color: '#34d399' },
    ],
  },
  {
    meta_key: 'project_budget',
    label: { he: 'תקציב פרויקט', en: 'Project Budget', ru: 'Бюджет проекта' },
    description: { he: 'תקציב כולל בשקלים', en: 'Total budget in ILS', ru: 'Общий бюджет в ILS' },
    field_type: 'number',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0 },
    default_value: null,
    icon: 'Wallet',
    category: 'construction',
    sort_order: 72,
  },
  {
    meta_key: 'main_contractor',
    label: { he: 'קבלן ראשי', en: 'Main Contractor', ru: 'Главный подрядчик' },
    description: { he: 'שם הקבלן הראשי', en: 'Main contractor name', ru: 'Имя главного подрядчика' },
    field_type: 'text',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'HardHat',
    category: 'construction',
    sort_order: 73,
  },
  {
    meta_key: 'subcontractors',
    label: { he: 'קבלני משנה', en: 'Subcontractors', ru: 'Субподрядчики' },
    description: { he: 'רשימת קבלני משנה', en: 'List of subcontractors', ru: 'Список субподрядчиков' },
    field_type: 'multi-select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'Users',
    category: 'construction',
    sort_order: 74,
  },
  {
    meta_key: 'permit_number',
    label: { he: 'מספר היתר', en: 'Permit Number', ru: 'Номер разрешения' },
    description: { he: 'מספר היתר בנייה', en: 'Building permit number', ru: 'Номер разрешения на строительство' },
    field_type: 'text',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { unique: true },
    default_value: null,
    icon: 'FileCheck',
    category: 'construction',
    sort_order: 75,
  },
  {
    meta_key: 'lot_number',
    label: { he: 'גוש/חלקה', en: 'Lot Number', ru: 'Номер участка' },
    description: { he: 'מספר גוש וחלקה', en: 'Block and parcel number', ru: 'Номер блока и участка' },
    field_type: 'text',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'Map',
    category: 'construction',
    sort_order: 76,
  },
  {
    meta_key: 'floor_count',
    label: { he: 'מספר קומות', en: 'Floor Count', ru: 'Этажность' },
    description: { he: 'מספר קומות במבנה', en: 'Number of floors', ru: 'Количество этажей' },
    field_type: 'number',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0 },
    default_value: null,
    icon: 'Building2',
    category: 'construction',
    sort_order: 77,
  },
  {
    meta_key: 'total_area_sqm',
    label: { he: 'שטח כולל (מ״ר)', en: 'Total Area (sqm)', ru: 'Общая площадь (м²)' },
    description: { he: 'שטח כולל במטרים רבועים', en: 'Total area in square meters', ru: 'Общая площадь в квадратных метрах' },
    field_type: 'number',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0 },
    default_value: null,
    icon: 'Ruler',
    category: 'construction',
    sort_order: 78,
  },
  {
    meta_key: 'construction_stage',
    label: { he: 'שלב בנייה', en: 'Construction Stage', ru: 'Этап строительства' },
    description: { he: 'שלב נוכחי בפרויקט', en: 'Current construction stage', ru: 'Текущий этап строительства' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'planning', label: { he: 'תכנון', en: 'Planning', ru: 'Планирование' }, color: '#94a3b8' },
      { value: 'permits', label: { he: 'היתרים', en: 'Permits', ru: 'Разрешения' }, color: '#a78bfa' },
      { value: 'foundation', label: { he: 'יסודות', en: 'Foundation', ru: 'Фундамент' }, color: '#60a5fa' },
      { value: 'structure', label: { he: 'שלד', en: 'Structure', ru: 'Каркас' }, color: '#22d3ee' },
      { value: 'envelope', label: { he: 'מעטפת', en: 'Envelope', ru: 'Оболочка' }, color: '#fbbf24' },
      { value: 'systems', label: { he: 'מערכות', en: 'Systems', ru: 'Системы' }, color: '#fb923c' },
      { value: 'finishing', label: { he: 'גמר', en: 'Finishing', ru: 'Отделка' }, color: '#f472b6' },
      { value: 'handover', label: { he: 'מסירה', en: 'Handover', ru: 'Передача' }, color: '#34d399' },
    ],
    validation: {},
    default_value: 'planning',
    icon: 'Layers',
    category: 'construction',
    sort_order: 79,
    color_rules: [
      { operator: 'eq', value: 'handover', color: '#34d399' },
      { operator: 'eq', value: 'planning', color: '#94a3b8' },
    ],
  },

  // ═══════════════════════════════════════════════════
  // ─── General — Missing Fields ──────────────────────
  // ═══════════════════════════════════════════════════
  {
    meta_key: 'currency',
    label: { he: 'מטבע', en: 'Currency', ru: 'Валюта' },
    description: { he: 'סוג מטבע', en: 'Currency type', ru: 'Тип валюты' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'ILS', label: { he: '₪ שקל', en: '₪ ILS', ru: '₪ ILS' } },
      { value: 'USD', label: { he: '$ דולר', en: '$ USD', ru: '$ USD' } },
      { value: 'EUR', label: { he: '€ יורו', en: '€ EUR', ru: '€ EUR' } },
    ],
    validation: {},
    default_value: 'ILS',
    icon: 'Coins',
    category: 'general',
    sort_order: 85,
  },
  {
    meta_key: 'country',
    label: { he: 'מדינה', en: 'Country', ru: 'Страна' },
    description: { he: 'מדינה', en: 'Country', ru: 'Страна' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'IL', label: { he: 'ישראל', en: 'Israel', ru: 'Израиль' } },
      { value: 'US', label: { he: 'ארה״ב', en: 'United States', ru: 'США' } },
      { value: 'UK', label: { he: 'בריטניה', en: 'United Kingdom', ru: 'Великобритания' } },
      { value: 'DE', label: { he: 'גרמניה', en: 'Germany', ru: 'Германия' } },
      { value: 'FR', label: { he: 'צרפת', en: 'France', ru: 'Франция' } },
      { value: 'RU', label: { he: 'רוסיה', en: 'Russia', ru: 'Россия' } },
    ],
    validation: {},
    default_value: 'IL',
    icon: 'Globe',
    category: 'general',
    sort_order: 86,
  },
  {
    meta_key: 'preferred_language',
    label: { he: 'שפה מועדפת', en: 'Preferred Language', ru: 'Предпочитаемый язык' },
    description: { he: 'שפת תקשורת מועדפת', en: 'Preferred communication language', ru: 'Предпочитаемый язык общения' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'he', label: { he: 'עברית', en: 'Hebrew', ru: 'Иврит' } },
      { value: 'en', label: { he: 'אנגלית', en: 'English', ru: 'Английский' } },
      { value: 'ru', label: { he: 'רוסית', en: 'Russian', ru: 'Русский' } },
      { value: 'ar', label: { he: 'ערבית', en: 'Arabic', ru: 'Арабский' } },
    ],
    validation: {},
    default_value: 'he',
    icon: 'Languages',
    category: 'general',
    sort_order: 87,
  },

  // ═══════════════════════════════════════════════════
  // ─── Finance — Invoice Fields ──────────────────────
  // ═══════════════════════════════════════════════════
  {
    meta_key: 'invoice_number',
    label: { he: 'מספר חשבונית', en: 'Invoice Number', ru: 'Номер счёта' },
    description: { he: 'מספר חשבונית מס', en: 'Tax invoice number', ru: 'Номер налогового счёта' },
    field_type: 'text',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { unique: true },
    default_value: null,
    icon: 'Receipt',
    category: 'finance',
    sort_order: 90,
  },
  {
    meta_key: 'invoice_amount',
    label: { he: 'סכום חשבונית', en: 'Invoice Amount', ru: 'Сумма счёта' },
    description: { he: 'סכום כולל מע״מ', en: 'Total amount including VAT', ru: 'Сумма включая НДС' },
    field_type: 'number',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0 },
    default_value: null,
    icon: 'DollarSign',
    category: 'finance',
    sort_order: 91,
  },
  {
    meta_key: 'invoice_date',
    label: { he: 'תאריך חשבונית', en: 'Invoice Date', ru: 'Дата счёта' },
    description: { he: 'תאריך הוצאת החשבונית', en: 'Invoice issue date', ru: 'Дата выставления счёта' },
    field_type: 'date',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'CalendarDays',
    category: 'finance',
    sort_order: 92,
  },
  {
    meta_key: 'payment_status',
    label: { he: 'סטטוס תשלום', en: 'Payment Status', ru: 'Статус оплаты' },
    description: { he: 'מצב תשלום החשבונית', en: 'Invoice payment status', ru: 'Статус оплаты счёта' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'pending', label: { he: 'ממתין', en: 'Pending', ru: 'Ожидание' }, color: '#fbbf24' },
      { value: 'partial', label: { he: 'חלקי', en: 'Partial', ru: 'Частичная' }, color: '#fb923c' },
      { value: 'paid', label: { he: 'שולם', en: 'Paid', ru: 'Оплачен' }, color: '#34d399' },
      { value: 'overdue', label: { he: 'באיחור', en: 'Overdue', ru: 'Просрочен' }, color: '#ef4444' },
      { value: 'cancelled', label: { he: 'מבוטל', en: 'Cancelled', ru: 'Отменён' }, color: '#94a3b8' },
    ],
    validation: {},
    default_value: 'pending',
    icon: 'CreditCard',
    category: 'finance',
    sort_order: 93,
    color_rules: [
      { operator: 'eq', value: 'overdue', color: '#ef4444' },
      { operator: 'eq', value: 'paid', color: '#34d399' },
    ],
  },
  {
    meta_key: 'payment_method',
    label: { he: 'אמצעי תשלום', en: 'Payment Method', ru: 'Способ оплаты' },
    description: { he: 'אמצעי התשלום', en: 'Payment method', ru: 'Способ оплаты' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'bank_transfer', label: { he: 'העברה בנקאית', en: 'Bank Transfer', ru: 'Банковский перевод' } },
      { value: 'check', label: { he: 'צ׳ק', en: 'Check', ru: 'Чек' } },
      { value: 'cash', label: { he: 'מזומן', en: 'Cash', ru: 'Наличные' } },
      { value: 'credit_card', label: { he: 'כרטיס אשראי', en: 'Credit Card', ru: 'Кредитная карта' } },
      { value: 'paypal', label: { he: 'PayPal', en: 'PayPal', ru: 'PayPal' } },
      { value: 'bit', label: { he: 'ביט', en: 'Bit', ru: 'Bit' } },
    ],
    validation: {},
    default_value: null,
    icon: 'Banknote',
    category: 'finance',
    sort_order: 94,
  },

  // ═══════════════════════════════════════════════════
  // ─── New Field Types — Currency / DateTime / Rating / Rich Text / File
  // ═══════════════════════════════════════════════════
  {
    meta_key: 'project_budget_nis',
    label: { he: 'תקציב פרויקט (₪)', en: 'Project Budget (₪)', ru: 'Бюджет проекта (₪)' },
    description: { he: 'תקציב כולל בשקלים חדשים', en: 'Total budget in NIS', ru: 'Общий бюджет в шекелях' },
    field_type: 'currency',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0 },
    default_value: null,
    icon: 'DollarSign',
    category: 'finance',
    sort_order: 95,
  },
  {
    meta_key: 'meeting_datetime',
    label: { he: 'תאריך ושעת פגישה', en: 'Meeting Date & Time', ru: 'Дата и время встречи' },
    description: { he: 'מועד מדויק של הפגישה', en: 'Exact meeting date and time', ru: 'Точная дата и время встречи' },
    field_type: 'datetime',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'CalendarClock',
    category: 'project',
    sort_order: 96,
  },
  {
    meta_key: 'contractor_rating',
    label: { he: 'דירוג קבלן', en: 'Contractor Rating', ru: 'Рейтинг подрядчика' },
    description: { he: 'דירוג איכות הקבלן (1-5)', en: 'Contractor quality rating (1-5)', ru: 'Рейтинг качества подрядчика (1-5)' },
    field_type: 'rating',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'Star',
    category: 'construction',
    sort_order: 97,
  },
  {
    meta_key: 'project_brief',
    label: { he: 'תקציר פרויקט', en: 'Project Brief', ru: 'Описание проекта' },
    description: { he: 'תיאור מפורט של הפרויקט', en: 'Detailed project description', ru: 'Подробное описание проекта' },
    field_type: 'rich_text',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'FileText',
    category: 'project',
    sort_order: 98,
  },
  {
    meta_key: 'contract_file_url',
    label: { he: 'קובץ חוזה', en: 'Contract File', ru: 'Файл контракта' },
    description: { he: 'קישור לקובץ החוזה', en: 'Link to contract file', ru: 'Ссылка на файл контракта' },
    field_type: 'file',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'Paperclip',
    category: 'business',
    sort_order: 99,
  },

  // ═══════════════════════════════════════════════════
  // ─── Case / Service Fields ──────────────────────────
  // ═══════════════════════════════════════════════════
  {
    meta_key: 'service_type',
    label: { he: 'סוג שירות', en: 'Service Type', ru: 'Тип услуги' },
    description: { he: 'סוג השירות שניתן ללקוח', en: 'Type of service provided', ru: 'Тип предоставляемой услуги' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'construction_management', label: { he: 'ניהול בנייה', en: 'Construction Management', ru: 'Управление строительством' }, color: '#60a5fa' },
      { value: 'project_oversight', label: { he: 'פיקוח פרויקט', en: 'Project Oversight', ru: 'Надзор за проектом' }, color: '#a78bfa' },
      { value: 'safety_inspection', label: { he: 'בדיקת בטיחות', en: 'Safety Inspection', ru: 'Проверка безопасности' }, color: '#f59e0b' },
      { value: 'consulting', label: { he: 'ייעוץ', en: 'Consulting', ru: 'Консалтинг' }, color: '#34d399' },
      { value: 'maintenance', label: { he: 'תחזוקה', en: 'Maintenance', ru: 'Обслуживание' }, color: '#fb923c' },
      { value: 'other', label: { he: 'אחר', en: 'Other', ru: 'Другое' }, color: '#94a3b8' },
    ],
    validation: {},
    default_value: null,
    icon: 'Wrench',
    category: 'business',
    sort_order: 100,
  },
  {
    meta_key: 'case_status',
    label: { he: 'שלב תיק', en: 'Case Status', ru: 'Статус дела' },
    description: { he: 'שלב נוכחי בצינור העבודה', en: 'Current pipeline stage', ru: 'Текущий этап воронки' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'inquiry', label: { he: 'פנייה', en: 'Inquiry', ru: 'Запрос' }, color: '#94a3b8' },
      { value: 'proposal', label: { he: 'הצעה', en: 'Proposal', ru: 'Предложение' }, color: '#a78bfa' },
      { value: 'negotiation', label: { he: 'משא ומתן', en: 'Negotiation', ru: 'Переговоры' }, color: '#60a5fa' },
      { value: 'signed', label: { he: 'חתום', en: 'Signed', ru: 'Подписан' }, color: '#22d3ee' },
      { value: 'in_progress', label: { he: 'בביצוע', en: 'In Progress', ru: 'В работе' }, color: '#fbbf24' },
      { value: 'completed', label: { he: 'הושלם', en: 'Completed', ru: 'Завершён' }, color: '#34d399' },
      { value: 'cancelled', label: { he: 'בוטל', en: 'Cancelled', ru: 'Отменён' }, color: '#ef4444' },
    ],
    validation: {},
    default_value: 'inquiry',
    icon: 'GitBranch',
    category: 'business',
    sort_order: 101,
    color_rules: [
      { operator: 'eq', value: 'completed', color: '#34d399' },
      { operator: 'eq', value: 'cancelled', color: '#ef4444' },
    ],
  },
  {
    meta_key: 'total_price',
    label: { he: 'מחיר כולל', en: 'Total Price', ru: 'Общая цена' },
    description: { he: 'מחיר כולל של השירות', en: 'Total service price', ru: 'Общая стоимость услуги' },
    field_type: 'currency',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0 },
    default_value: null,
    icon: 'DollarSign',
    category: 'finance',
    sort_order: 102,
  },
  {
    meta_key: 'paid_amount',
    label: { he: 'סכום ששולם', en: 'Paid Amount', ru: 'Оплаченная сумма' },
    description: { he: 'סכום שכבר שולם', en: 'Amount already paid', ru: 'Уже оплаченная сумма' },
    field_type: 'currency',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { min: 0 },
    default_value: 0,
    icon: 'Wallet',
    category: 'finance',
    sort_order: 103,
  },
  {
    meta_key: 'agreement_ref',
    label: { he: 'קישור להסכם', en: 'Agreement Link', ru: 'Ссылка на договор' },
    description: { he: 'קישור למסמך ההסכם', en: 'Link to agreement document', ru: 'Ссылка на документ договора' },
    field_type: 'url',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'ExternalLink',
    category: 'business',
    sort_order: 104,
  },

  // ═══════════════════════════════════════════════════
  // ─── Contractor Registration Fields ──────────────────
  // ═══════════════════════════════════════════════════
  {
    meta_key: 'contractor_license_number',
    label: { he: 'מספר רישיון קבלן', en: 'Contractor License Number', ru: 'Номер лицензии подрядчика' },
    description: { he: 'מספר רישיון קבלן רשום', en: 'Registered contractor license number', ru: 'Номер зарегистрированной лицензии подрядчика' },
    field_type: 'text',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: { required: true, unique: true },
    default_value: null,
    icon: 'FileCheck',
    category: 'construction',
    sort_order: 110,
  },
  {
    meta_key: 'contractor_classification',
    label: { he: 'דרגת סיווג', en: 'Classification Tier', ru: 'Уровень классификации' },
    description: { he: 'דרגת הסיווג בפנקס הקבלנים', en: 'Classification tier in the contractor registry', ru: 'Уровень классификации в реестре подрядчиков' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'gimel_1', label: { he: "ג׳1", en: 'C1', ru: 'Г1' }, color: '#94a3b8' },
      { value: 'gimel_2', label: { he: "ג׳2", en: 'C2', ru: 'Г2' }, color: '#94a3b8' },
      { value: 'gimel_3', label: { he: "ג׳3", en: 'C3', ru: 'Г3' }, color: '#a78bfa' },
      { value: 'gimel_4', label: { he: "ג׳4", en: 'C4', ru: 'Г4' }, color: '#a78bfa' },
      { value: 'gimel_5', label: { he: "ג׳5", en: 'C5', ru: 'Г5' }, color: '#a78bfa' },
      { value: 'bet_1', label: { he: "ב׳1", en: 'B1', ru: 'Б1' }, color: '#60a5fa' },
      { value: 'bet_2', label: { he: "ב׳2", en: 'B2', ru: 'Б2' }, color: '#60a5fa' },
      { value: 'bet_3', label: { he: "ב׳3", en: 'B3', ru: 'Б3' }, color: '#60a5fa' },
      { value: 'bet_4', label: { he: "ב׳4", en: 'B4', ru: 'Б4' }, color: '#22d3ee' },
      { value: 'bet_5', label: { he: "ב׳5", en: 'B5', ru: 'Б5' }, color: '#22d3ee' },
      { value: 'alef_1', label: { he: "א׳1", en: 'A1', ru: 'А1' }, color: '#34d399' },
      { value: 'alef_2', label: { he: "א׳2", en: 'A2', ru: 'А2' }, color: '#34d399' },
      { value: 'alef_3', label: { he: "א׳3", en: 'A3', ru: 'А3' }, color: '#34d399' },
      { value: 'alef_4', label: { he: "א׳4", en: 'A4', ru: 'А4' }, color: '#fbbf24' },
      { value: 'alef_5', label: { he: "א׳5", en: 'A5', ru: 'А5' }, color: '#fbbf24' },
      { value: 'unlimited', label: { he: 'בלתי מוגבל', en: 'Unlimited', ru: 'Без ограничений' }, color: '#f59e0b' },
    ],
    validation: {},
    default_value: null,
    icon: 'Award',
    category: 'construction',
    sort_order: 111,
  },
  {
    meta_key: 'classification_category',
    label: { he: 'ענפי סיווג', en: 'Classification Categories', ru: 'Категории классификации' },
    description: { he: 'ענפי הסיווג של הקבלן', en: 'Contractor classification branches', ru: 'Отрасли классификации подрядчика' },
    field_type: 'multi-select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'skeleton', label: { he: 'שלד', en: 'Skeleton/Structure', ru: 'Каркас' }, color: '#60a5fa' },
      { value: 'finishing', label: { he: 'גמר', en: 'Finishing', ru: 'Отделка' }, color: '#a78bfa' },
      { value: 'infrastructure', label: { he: 'תשתיות', en: 'Infrastructure', ru: 'Инфраструктура' }, color: '#22d3ee' },
      { value: 'electrical', label: { he: 'חשמל', en: 'Electrical', ru: 'Электрика' }, color: '#fbbf24' },
      { value: 'plumbing', label: { he: 'אינסטלציה', en: 'Plumbing', ru: 'Сантехника' }, color: '#34d399' },
      { value: 'hvac', label: { he: 'מיזוג אוויר', en: 'HVAC', ru: 'Кондиционирование' }, color: '#fb923c' },
      { value: 'elevators', label: { he: 'מעליות', en: 'Elevators', ru: 'Лифты' }, color: '#f472b6' },
      { value: 'waterproofing', label: { he: 'איטום', en: 'Waterproofing', ru: 'Гидроизоляция' }, color: '#94a3b8' },
      { value: 'roads', label: { he: 'כבישים', en: 'Roads', ru: 'Дороги' }, color: '#818cf8' },
      { value: 'landscaping', label: { he: 'פיתוח סביבתי', en: 'Landscaping', ru: 'Ландшафт' }, color: '#10b981' },
      { value: 'demolition', label: { he: 'הריסות', en: 'Demolition', ru: 'Снос' }, color: '#ef4444' },
      { value: 'special', label: { he: 'עבודות מיוחדות', en: 'Special Works', ru: 'Спецработы' }, color: '#8b5cf6' },
    ],
    validation: {},
    default_value: null,
    icon: 'Layers',
    category: 'construction',
    sort_order: 112,
  },
  {
    meta_key: 'license_expiry_date',
    label: { he: 'תוקף רישיון', en: 'License Expiry Date', ru: 'Срок действия лицензии' },
    description: { he: 'תאריך פקיעת רישיון הקבלן', en: 'Contractor license expiry date', ru: 'Дата истечения лицензии подрядчика' },
    field_type: 'date',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'CalendarX',
    category: 'construction',
    sort_order: 113,
    color_rules: [
      { operator: 'lt', value: 'today', color: '#ef4444' },
    ],
  },
  {
    meta_key: 'insurance_expiry_date',
    label: { he: 'תוקף ביטוח', en: 'Insurance Expiry Date', ru: 'Срок действия страховки' },
    description: { he: 'תאריך פקיעת תעודת הביטוח', en: 'Insurance certificate expiry date', ru: 'Дата истечения страхового свидетельства' },
    field_type: 'date',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [],
    validation: {},
    default_value: null,
    icon: 'ShieldCheck',
    category: 'construction',
    sort_order: 114,
    color_rules: [
      { operator: 'lt', value: 'today', color: '#ef4444' },
    ],
  },
  {
    meta_key: 'registration_status',
    label: { he: 'שלב רישום', en: 'Registration Status', ru: 'Статус регистрации' },
    description: { he: 'שלב התהליך ברישום קבלן', en: 'Contractor registration process stage', ru: 'Этап процесса регистрации подрядчика' },
    field_type: 'select',
    is_composite: false,
    sub_fields: [],
    display_template: null,
    options: [
      { value: 'inquiry', label: { he: 'פנייה ראשונית', en: 'Inquiry', ru: 'Запрос' }, color: '#94a3b8' },
      { value: 'documents_received', label: { he: 'מסמכים התקבלו', en: 'Documents Received', ru: 'Документы получены' }, color: '#a78bfa' },
      { value: 'in_review', label: { he: 'בבדיקה', en: 'In Review', ru: 'На рассмотрении' }, color: '#60a5fa' },
      { value: 'submitted', label: { he: 'הוגש לרשם', en: 'Submitted', ru: 'Подано' }, color: '#fbbf24' },
      { value: 'approved', label: { he: 'אושר', en: 'Approved', ru: 'Одобрено' }, color: '#34d399' },
      { value: 'rejected', label: { he: 'נדחה', en: 'Rejected', ru: 'Отклонено' }, color: '#ef4444' },
    ],
    validation: {},
    default_value: 'inquiry',
    icon: 'ClipboardCheck',
    category: 'construction',
    sort_order: 115,
    color_rules: [
      { operator: 'eq', value: 'approved', color: '#34d399' },
      { operator: 'eq', value: 'rejected', color: '#ef4444' },
    ],
  },
];

// ─── Built-in Field Groups ──────────────────────────
import type { FieldGroupInsert } from './types';

export const BUILTIN_FIELD_GROUPS: FieldGroupInsert[] = [
  {
    meta_key: 'work_experience',
    label: { he: 'ניסיון תעסוקתי', en: 'Work Experience', ru: 'Опыт работы' },
    description: { he: 'רשומות ניסיון עבודה חוזרות', en: 'Repeating work experience entries', ru: 'Записи об опыте работы' },
    field_refs: ['business_name', 'role', 'start_date', 'end_date'],
    icon: 'Briefcase',
    category: 'hr',
    sort_order: 0,
  },
  {
    meta_key: 'call_log',
    label: { he: 'לוג שיחות', en: 'Call Log', ru: 'Журнал звонков' },
    description: { he: 'תיעוד שיחות חוזר', en: 'Repeating call documentation', ru: 'Журнал документации звонков' },
    field_refs: ['date', 'summary', 'action', 'caller'],
    icon: 'PhoneCall',
    category: 'general',
    sort_order: 1,
  },
  {
    meta_key: 'education',
    label: { he: 'השכלה', en: 'Education', ru: 'Образование' },
    description: { he: 'רשומות השכלה חוזרות', en: 'Repeating education entries', ru: 'Записи об образовании' },
    field_refs: ['institution', 'degree', 'year'],
    icon: 'GraduationCap',
    category: 'hr',
    sort_order: 2,
  },
  {
    meta_key: 'payment_log',
    label: { he: 'לוג תשלומים', en: 'Payment Log', ru: 'Журнал платежей' },
    description: { he: 'תיעוד תשלומים חוזר — תאריך, סכום, אמצעי, אסמכתא', en: 'Repeating payment entries — date, amount, method, reference', ru: 'Журнал платежей — дата, сумма, метод, ссылка' },
    field_refs: ['date', 'amount', 'method', 'reference'],
    icon: 'Receipt',
    category: 'finance',
    sort_order: 3,
  },
  {
    meta_key: 'document_log',
    label: { he: 'לוג מסמכים', en: 'Document Log', ru: 'Журнал документов' },
    description: { he: 'תיעוד מסמכים מצורפים — סוג, כותרת, תאריך, קישור', en: 'Attached documents — type, title, date, link', ru: 'Прикреплённые документы — тип, заголовок, дата, ссылка' },
    field_refs: ['doc_type', 'doc_title', 'upload_date', 'url'],
    icon: 'FileStack',
    category: 'general',
    sort_order: 4,
  },
  {
    meta_key: 'meeting_log',
    label: { he: 'לוג פגישות', en: 'Meeting Log', ru: 'Журнал встреч' },
    description: { he: 'תיעוד פגישות — תאריך, משתתפים, סיכום, פעולות', en: 'Meeting records — date, attendees, summary, action items', ru: 'Записи встреч — дата, участники, итоги, действия' },
    field_refs: ['date', 'attendees', 'summary', 'action_items'],
    icon: 'CalendarCheck',
    category: 'general',
    sort_order: 5,
  },
];
